import PageLayout from "components/layout/PageLayout";
import { useDeleteDeviceMutation, useLazyGetDevicesStatusQuery, useSetDeviceStatusMutation } from "./api/devicesStatusApiSlice";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useWebSocket } from "app/ws/WebSocketProvider";
import { selectCompanyGuid } from "features/auth/authSlice";
import { useSelector } from "react-redux";
import { Dialog } from "primereact/dialog";
import DeviceStats from "../views/DeviceStats";
import NoData from "components/commons/NoData";
import DeviceCard from "./view/DeviceCard";


function DeviceDashboard({ }) {

    const [t] = useTranslation(["global", "device"])
    const { connectionStatus: wsStatus, client: wsClient } = useWebSocket()
    const companyGuid = useSelector(selectCompanyGuid)

    const [autoRefresh, setAutoRefresh] = useState(true)

    const canManageDevices = true

    const refreshSeconds = 30

    const [getStatus, {
        data: devices,
        isFetching,
        isLoading,
        isSuccess,
        isError,
        error
    }] = useLazyGetDevicesStatusQuery();


    const [
        setStatusRequest, {
            isLoading: deviceStatusChangeIsLoading,
            isSuccess: deviceStatusChangeIsIsSuccess
        }
    ] = useSetDeviceStatusMutation()

    useEffect(() => {
        getStatus(); // Recupera i dati al primo render

        if (autoRefresh) {
            const interval = setInterval(() => {
                getStatus();
            }, refreshSeconds * 1000);

            return () => clearInterval(interval); // Pulisce l'intervallo quando il componente si smonta
        }
    }, [getStatus, autoRefresh]);


    function DeviceStatusBadge({ device }) {

        let color
        let textColor = "white"
        switch (device.smartStatus) {
            case "CHARGING": color = "bg-primary"
                break
            case "OFFBODY": color = "bg-warning"; textColor = "dark"
                break
            case "IN_USE": color = "bg-success"
                break
            case "DISABLED": color = "bg-danger"
                break
            default: color = "dark"
        }

        return <span className={`badge rounded-pill ${color} text-${textColor}`}>
            {device.status.isCharging && <i className="fa-solid fa-bolt"></i>}{" "}
            {t(`device:entity.device.smart_status.${device.smartStatus}`).toUpperCase()}
        </span>
    }


    // Funzione per determinare la classe colore per l'icona della batteria
    const getBatteryClass = (device) => {
        if (device.status.isCharging)
            return "#0d6efd" // blu
        if (device.status.batteryLevel < 15) return "#cd3342"; // rosso
        if (device.status.batteryLevel < 25) return "orange";  // arancio
        if (device.status.batteryLevel < 50) return "#ffc107";  // giallo
        return "#198754";                         // verde
    };



    const handleAction = (deviceGuid, actionType) => {
        console.log(`Required ${actionType} for ${deviceGuid}`)
        let command = {
            type: actionType
        }
        try {
            wsClient.publish(`company/${companyGuid}/device/${deviceGuid}/request`, JSON.stringify(command))
        } catch (err) {
            console.error(`Error trying to publish mqtt command`)
        }
    }


    const handleChangeStatus = (e, deviceGuid) => {
        console.log("Set status: " + e.target.checked + " on deviceGuid: " + deviceGuid)
        setStatusRequest({
            deviceGuid: deviceGuid,
            enabled: e.target.checked
        })
    }

    const DeleteDeviceTemplate = ({ device, btnClassName = "btn btn-danger" }) => {
        const [t] = useTranslation(["global", "device"]);
        const [openDialog, setOpenDialog] = useState(false);
        const [deleteDevice] = useDeleteDeviceMutation()
        return (
            <>
                <button className={btnClassName} onClick={() => setOpenDialog(true)}><i className="fa-solid fa-trash"></i> {t("action.delete")}</button>
                <Dialog header={t("device:actions.delete_device")} visible={openDialog} onHide={() => setOpenDialog(false)} >
                    <span>{t("device:helper.device_delete_confirm")} <b>{device.guid.substr(device.guid.length - 5)}</b> ?</span>
                    <div className="text-end mt-4">
                        <button className="btn btn-danger mx-2" onClick={() => {
                            deleteDevice(device.guid)
                            setOpenDialog(false)
                        }}>  {t("action.delete")} </button>
                        <button className="btn btn-secondary" onClick={() => setOpenDialog(false)}>{t("action.cancel")}</button>
                    </div>
                </Dialog>
            </>
        );
    };

    let content = (
        <>

            <div className="d-flex justify-content-end mb-3 form-check form-switch">
                <input
                    type="checkbox"
                    className="form-check-input"
                    checked={autoRefresh}
                    onChange={() => setAutoRefresh(!autoRefresh)}
                />
                <label className="form-check-label ms-2">Auto-Refresh <span className="text-muted">{refreshSeconds}s</span></label>
            </div>
            {isSuccess &&
                < div className="container mt-4">
                    {devices.length > 0 ? (
                        <>
                            <DeviceStats devices={devices} />
                            <table className="table table-dark table-hover align-middle ">
                                <thead>
                                    <tr>
                                        <th scope="col">ID</th>
                                        <th scope="col">{t("device:entity.device.props.enabled")}</th>
                                        <th scope="col">IP</th>
                                        <th scope="col">{t("device:entity.device.props.model")}</th>
                                        <th scope="col">{t("device:entity.device.props.battery")}</th>
                                        <th scope="col">{t("device:entity.device.props.state")}</th>
                                        <th scope="col">{t("device:entity.device.props.info")}</th>
                                        <th scope="col">{t("device:entity.device.props.last_user")}</th>
                                        <th scope="col">{t("property.actions")}</th>

                                    </tr>
                                </thead>
                                <tbody>
                                    {devices.map((device) => (
                                        <DeviceCard
                                            key={`${device.guid}`}
                                            device={device}
                                            canManageDevices={canManageDevices}
                                            handleAction={handleAction}
                                            handleChangeStatus={handleChangeStatus}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </>
                    ) : <NoData message={t("device:helper.no_devices")} />
                    }
                </div>
            }
        </>
    );


    return (
        <PageLayout
            pageTitle={t("device:menu.devices_manager")}
            bodyContent={content}
            isLoadingPage={isLoading}
            isLoadingContent={deviceStatusChangeIsLoading}
            isError={isError}
            errorMsg={error}
            onPageRefresh={getStatus}
        />
    );
};

export default DeviceDashboard;